import { useSyncExternalStore } from 'react';
import { defaultsFromSchema } from '../settings/schema';
import { defaultSlots } from '../settings/filaments';
import { P1S } from '../settings/schema';
import type { AppState, PlacedObject, ToolMode } from './types';

type Listener = () => void;

let state: AppState = {
  objects: [],
  selectedId: null,
  settings: defaultsFromSchema(),
  visibility: 'basic',
  tool: 'select',
  brushSize: 4,
  overhangHighlight: false,
  overhangAngle: 30,
  filaments: defaultSlots(),
  activeFilament: 0,
  projectName: 'Untitled',
  settingsOpen: false,
};

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

function setState(partial: Partial<AppState> | ((s: AppState) => Partial<AppState>)) {
  const patch = typeof partial === 'function' ? partial(state) : partial;
  state = { ...state, ...patch };
  emit();
}

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function bboxOf(positions: Float32Array, scale: [number, number, number]) {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const [sx, sy, sz] = scale;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i] * sx;
    const y = positions[i + 1] * sy;
    const z = positions[i + 2] * sz;
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
  }
  return { minX, minY, minZ, maxX, maxY, maxZ, sx: maxX - minX, sy: maxY - minY, sz: maxZ - minZ };
}

export const store = {
  getSnapshot: () => state,
  subscribe: (l: Listener) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  set: setState,

  addMesh(name: string, positions: Float32Array) {
    const id = uid('obj');
    const scale: [number, number, number] = [1, 1, 1];
    // Normalise mesh: centre XZ on origin, drop base to Y=0 (Y-up)
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < positions.length; i += 3) {
      minX = Math.min(minX, positions[i]); maxX = Math.max(maxX, positions[i]);
      minY = Math.min(minY, positions[i + 1]); maxY = Math.max(maxY, positions[i + 1]);
      minZ = Math.min(minZ, positions[i + 2]); maxZ = Math.max(maxZ, positions[i + 2]);
    }
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const normalised = new Float32Array(positions.length);
    for (let i = 0; i < positions.length; i += 3) {
      normalised[i] = positions[i] - cx;
      normalised[i + 1] = positions[i + 1] - minY;
      normalised[i + 2] = positions[i + 2] - cz;
    }
    const obj: PlacedObject = {
      id,
      name,
      positions: normalised,
      position: [P1S.bedX / 2, P1S.bedY / 2, 0],
      rotation: [0, 0, 0],
      scale,
      overrides: {},
      extruder: state.activeFilament,
      paintSupports: new Uint8Array(normalised.length / 9),
    };
    setState({
      objects: [...state.objects, obj],
      selectedId: id,
      projectName:
        state.objects.length === 0 && state.projectName === 'Untitled' ? name : state.projectName,
    });
    return id;
  },

  select(id: string | null) {
    setState({ selectedId: id });
  },

  updateObject(id: string, patch: Partial<PlacedObject>) {
    setState({
      objects: state.objects.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });
  },

  deleteSelected() {
    if (!state.selectedId) return;
    const objects = state.objects.filter((o) => o.id !== state.selectedId);
    setState({ objects, selectedId: objects[0]?.id ?? null });
  },

  duplicateSelected() {
    const src = state.objects.find((o) => o.id === state.selectedId);
    if (!src) return;
    const copy: PlacedObject = {
      ...src,
      id: uid('obj'),
      name: `${src.name} copy`,
      position: [src.position[0] + 15, src.position[1] + 15, src.position[2]],
      overrides: { ...src.overrides },
      paintSupports: src.paintSupports ? new Uint8Array(src.paintSupports) : undefined,
      positions: src.positions, // share geometry buffer
    };
    setState({ objects: [...state.objects, copy], selectedId: copy.id });
  },

  setSetting(id: string, value: string | number | boolean) {
    setState({ settings: { ...state.settings, [id]: value } });
  },

  setSettings(values: Record<string, string | number | boolean>) {
    setState({ settings: { ...values } });
  },

  setVisibility(visibility: AppState['visibility']) {
    setState({ visibility });
  },

  setTool(tool: ToolMode) {
    setState({ tool });
  },

  dropSelectedToBed() {
    const o = state.objects.find((x) => x.id === state.selectedId);
    if (!o) return;
    const bb = bboxOf(o.positions, o.scale);
    // After rotation this is approximate; good enough for v1
    store.updateObject(o.id, {
      position: [o.position[0], o.position[1], -bb.minY],
    });
  },

  centreSelected() {
    const o = state.objects.find((x) => x.id === state.selectedId);
    if (!o) return;
    store.updateObject(o.id, {
      position: [P1S.bedX / 2, P1S.bedY / 2, o.position[2]],
    });
  },

  autoArrange() {
    const gap = 8;
    let x = 20;
    let y = 20;
    let rowH = 0;
    const next = state.objects.map((o) => {
      const bb = bboxOf(o.positions, o.scale);
      if (x + bb.sx > P1S.bedX - 10) {
        x = 20;
        y += rowH + gap;
        rowH = 0;
      }
      const pos: [number, number, number] = [x + bb.sx / 2, y + bb.sz / 2, -bb.minY];
      x += bb.sx + gap;
      rowH = Math.max(rowH, bb.sz);
      return { ...o, position: pos };
    });
    setState({ objects: next });
  },

  setObjectOverride(id: string, key: string, value: string | number | boolean | undefined) {
    setState({
      objects: state.objects.map((o) => {
        if (o.id !== id) return o;
        const overrides = { ...o.overrides };
        if (value === undefined) delete overrides[key];
        else overrides[key] = value;
        return { ...o, overrides };
      }),
    });
  },

  paintTriangles(objectId: string, triangleIndices: number[], value: 0 | 1 | 2) {
    setState({
      objects: state.objects.map((o) => {
        if (o.id !== objectId) return o;
        const triCount = o.positions.length / 9;
        const paint = o.paintSupports
          ? new Uint8Array(o.paintSupports)
          : new Uint8Array(triCount);
        for (const ti of triangleIndices) {
          if (ti >= 0 && ti < paint.length) paint[ti] = value;
        }
        return { ...o, paintSupports: paint };
      }),
    });
  },
};

export function useAppStore<T>(selector: (s: AppState) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getSnapshot()),
    () => selector(store.getSnapshot())
  );
}

export function objectBounds(o: PlacedObject) {
  return bboxOf(o.positions, o.scale);
}

export function isOutOfBounds(o: PlacedObject): boolean {
  const bb = bboxOf(o.positions, o.scale);
  const halfX = bb.sx / 2;
  const halfY = bb.sz / 2;
  const x0 = o.position[0] - halfX;
  const x1 = o.position[0] + halfX;
  const y0 = o.position[1] - halfY;
  const y1 = o.position[1] + halfY;
  return x0 < 0 || y0 < 0 || x1 > P1S.bedX || y1 > P1S.bedY || bb.sy > P1S.bedZ;
}
